<template>
  <div>
    <div class="bg-white shadow-md rounded-md overflow-hidden">
      <div class="border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <h2 class="text-lg font-semibold text-gray-900">{{ getPageTitle }}</h2>
        <CustomButton v-if="showDetailView" :buttonText="t('socialMedia.components.posts.buttons.backToList')" color="secondary" size="sm" @click="hidePostDetail"/>
      </div>

      <div v-if="!showDetailView">
        <CustomTable 
          :data="myData" 
          :columns="tableColumns" 
          :is-loading="isInitialLoading || isLoading"
          :min-width="'100%'"
          v-bind="tableProps" 
          @row-click="handleRowClick" 
          @search="handleSearch" 
          @sort="handleSort" 
          @filter="handleFilter" 
          @page-change="handlePageChange"
        >
          <template #table-actions>
            <CustomButton icon="add" iconPosition="right" @click="createNewContent" :buttonText="t('socialMedia.components.posts.buttons.newPost')" size="sm"/>
          </template>

          <template #platform="{ item }">
            <div class="flex items-center justify-center gap-1">
              <div v-for="(r, index) in item.platforms" :key="index">
                <img v-if="r.platformId" :src="getPlatformIcon(r.platformId)" :alt="r.platformId" class="w-5 h-5 rounded-full"/>
                <span v-else class="material-symbols-outlined !text-[20px]">share</span>
              </div>
            </div>
          </template>

          <template #content="{ item }">
            <p class="text-sm text-gray-900 truncate">{{ truncateMessage(item.message, 100) }}</p>
          </template>

          <template #status="{ item }">
            <div class="flex items-center justify-center text-xs w-full" :class="statusClass(item.isPublished)">
              <span class="material-symbols-outlined !text-[12px]">{{ getStatusIcon(item.isPublished) }}</span>
              <p class="text-center w-16">{{ getStatusText(item.isPublished) }}</p>
            </div>
          </template>

          <template #date="{ item }">
            <div class="flex justify-center text-center">
              <span class="material-symbols-outlined !text-[16px]">calendar_month</span>
              <p class="w-36 text-xs">
                <time :datetime="item.publishedDate">{{
                    formatDate(item.publishedDate)
                  }}
                </time>
              </p>
            </div>
          </template>
        </CustomTable>
      </div>

      <div v-else-if="showDetailView">
        <PostInspector 
          :commentId="commentId || null" 
          :postId="socialPostsStore.selectedPostId || null" 
          @update:isPublished="updateIsPublished" 
          @close="hidePostDetail"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, watch, ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter, useRoute } from 'vue-router'
import { useAssetStore } from '@/stores/asset'
import { useSocialPostsStore } from '@/stores/social-posts'
import { dateHelper } from '@/utils/dateHelper'
import { responseHandlerService } from '@/plugins/responseHandlerService'
import CustomTable from '@/components/table/VTable.vue'
import PostInspector from './components/postInspector/index.vue'
import CustomButton from '@/components/base/buttons/VButton.vue'

// Content list component should not inherit attrs
defineOptions({
  inheritAttrs: false
})

// Enums
const PostStatusEnum = {
  DRAFT: 1,
  PUBLISHED: 2,
}

const PlatformEnum = {
  INSTAGRAM: 1,
  FACEBOOK: 2,
  TWITTER: 3,
}

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const assetStore = useAssetStore()
const socialPostsStore = useSocialPostsStore()

// State
const isInitialLoading = ref(true)
const isLoading = ref(false)
const myData = ref([])
const currentPage = ref(socialPostsStore.currentPage)
const pageCount = ref(socialPostsStore.pageCount)
const totalCount = ref(socialPostsStore.totalCount)
const itemsPerPage = ref(15)
const sortKey = ref(socialPostsStore.sortKey)
const sortOrder = ref(socialPostsStore.sortOrder)
const searchQuery = ref(socialPostsStore.searchQuery)
const selectedFilter = ref(socialPostsStore.selectedFilter)
const commentId = ref(null)

// Table Configuration
const tableColumns = computed(() => [
  { key: 'platform', label: t('socialMedia.components.posts.tableHeaders.platform'), sortable: false, width: 'w-1/12', align: 'center' },
  { key: 'content', label: t('socialMedia.components.posts.tableHeaders.content'), sortable: false, width: 'w-4/12', align: 'left' },
  { key: 'status', label: t('socialMedia.components.posts.tableHeaders.status'), sortable: false, width: 'w-2/12', align: 'center' },
  { key: 'date', label: t('socialMedia.components.posts.tableHeaders.date'), sortable: true, width: 'w-3/12', align: 'center' },
])

const filterItems = [
  { id: 1, name: t('socialMedia.components.posts.filterItems.all') },
  { id: 2, name: t('socialMedia.components.posts.filterItems.published') },
  { id: 3, name: t('socialMedia.components.posts.filterItems.draft') },
]

// Computed
const tableProps = computed(() => ({
  isLoading: isLoading.value,
  itemsPerPage: itemsPerPage.value,
  totalCount: totalCount.value,
  pageCount: pageCount.value,
  currentPage: currentPage.value,
  sortKey: sortKey.value,
  sortOrder: sortOrder.value,
  searchPlaceholder: t('socialMedia.components.posts.searchPlaceholder'),
  showSearch: true,
  showFilter: true,
  filterOptions: [{
    id: 'status',
    icon: 'filter_alt',
    iconClass: 'material-symbols-outlined text-gray-600 !text-[22px]',
    showText: false,
    items: filterItems,
  }],
  minWidth: '80vh',
  minHeight: '53vh',
  maxHeight: '53vh',
}))

const getPageTitle = computed(() => {
  return showDetailView.value
    ? t('socialMedia.components.posts.pageTitle.contentInspection')
    : t('socialMedia.components.posts.pageTitle.contentManagement')
})

// Computed properties
const showDetailView = computed(() => {
  return socialPostsStore.selectedPostId !== null
})

// Utility functions for status
const getStatusIcon = (status) => {
  return status === PostStatusEnum.PUBLISHED ? 'published_with_changes' : 'pending'
}

const getStatusText = (status) => {
  return status === PostStatusEnum.PUBLISHED 
    ? t('socialMedia.components.posts.status.published')
    : t('socialMedia.components.posts.status.draft')
}

// Utility functions
const statusClass = (status) => {
  switch (status) {
    case PostStatusEnum.PUBLISHED:
      return 'text-green-500'
    case PostStatusEnum.DRAFT:
      return 'text-blue-500'
    default:
      return 'text-gray-500'
  }
}

const formatDate = (dateString) => {
  return dateString 
    ? dateHelper.formatLocalized(dateString, 'medium', { type: 'datetime' })
    : t('socialMedia.components.posts.unknown')
}

const getPlatformIcon = (platformId) => {
  const platformIcons = {
    [PlatformEnum.INSTAGRAM]: '/images/social/instagram.svg',
    [PlatformEnum.FACEBOOK]: '/images/social/facebook.svg',
    [PlatformEnum.TWITTER]: '/images/social/twitter.svg',
  }
  return platformIcons[platformId] || '/images/social/default.svg'
}

const truncateMessage = (message, length = 100) => {
  if (!message) return ''
  return message.length > length 
    ? message.substring(0, length) + '...'
    : message
}

const fetchData = async (forceRefresh = false) => {
  try {
    isLoading.value = true
    
    // Görünüm değişikliği olduğunda veya force refresh isteniyorsa
    // forceRefresh parametresini true olarak gönder
    const shouldForceRefresh = forceRefresh || socialPostsStore.lastActiveView === 'detail';
    
    // Store'un önbellek kontrol metodunu kullan
    const useCachedData = socialPostsStore.isPostsListFresh(shouldForceRefresh);
    
    if (useCachedData && socialPostsStore.postsList.length > 0) {
      myData.value = socialPostsStore.postsList;
      totalCount.value = socialPostsStore.totalCount;
      pageCount.value = socialPostsStore.pageCount;
      isLoading.value = false;
      isInitialLoading.value = false;
      return;
    }
    
    // Store'un fetch metodunu kullan
    const result = await socialPostsStore.fetchPostsList(assetStore.assetId, {
      q: searchQuery.value,
      page: currentPage.value - 1,
      sort: sortKey.value,
      order: sortOrder.value,
      filter: selectedFilter.value,
      forceRefresh: shouldForceRefresh // Önbelleklemeyi bypass etmek için
    })
    
    // Verileri güncelle
    myData.value = result.detail
    totalCount.value = result.totalCount
    pageCount.value = result.pageCount
    
  } catch (err) {
    responseHandlerService.handleError(err, t('socialMedia.components.posts.fetchError'))
    isLoading.value = false
    isInitialLoading.value = false
  } finally {
    isLoading.value = false
    isInitialLoading.value = false
  }
}

const handleSearch = (query) => {
  searchQuery.value = query
  currentPage.value = 1
  socialPostsStore.setSearchParams({ searchQuery: query, page: 1 })
  fetchData()
}

const handleFilter = ({ itemId }) => {
  selectedFilter.value = itemId
  currentPage.value = 1
  socialPostsStore.setSearchParams({ selectedFilter: itemId, page: 1 })
  fetchData()
}

const handleSort = ({ key, order }) => {
  sortKey.value = key
  sortOrder.value = order
  socialPostsStore.setSearchParams({ sortKey: key, sortOrder: order })
  fetchData()
}

const handlePageChange = (page) => {
  currentPage.value = page
  socialPostsStore.setSearchParams({ page })
  fetchData()
}

const handleRowClick = (item) => {
  // Store'daki showPostDetail fonksiyonunu kullan
  socialPostsStore.showPostDetail(router, item.id)
  commentId.value = item.postId || null
}

const hidePostDetail = () => {
  // Store'daki hidePostDetail fonksiyonunu kullan
  socialPostsStore.hidePostDetail(router)
  commentId.value = null
}

const createNewContent = () => {
  router.push({ query: { ...route.query, tab: 'new-content' } })
}

const updateIsPublished = (isPublished) => {
  // İçeriğin yayınlama durumunu güncelleme (gerekirse implement et)
}

// Sayfa yüklenirken parametreleri kontrol et
onMounted(() => {
  // View parametresini kontrol et
  const view = route.query.view
  
  // Sayfa yüklenirken store'daki view bilgisini kontrol et
  // Eğer detaydan listeye dönüyorsak, forceRefresh ile çağır
  const forceRefresh = socialPostsStore.lastActiveView === 'detail';
  
  // Verileri getir, ama view=detail ise listeyi göstermeyeceğiz
  fetchData(forceRefresh)
  
  // Görünümü güncelle
  socialPostsStore.lastActiveView = 'list'
})
</script>